import { createAuthClient } from "better-auth/react";
import { env } from "@/app/config/env";
import { secureStore } from "@/app/platform";

/**
 * SESSIONS ON A PHONE ARE BEARER TOKENS, NOT COOKIES.
 *
 * The web view serves the app from `capacitor://localhost` while the service lives on
 * https — a cross-site pair, where a cookie needs `SameSite=None; Secure` and is then
 * subject to WebKit's tracking-prevention rules and to being dropped on app relaunch.
 * People discover this as "it logs me out sometimes", which is unfixable from the app.
 *
 * So the service runs the auth library's bearer mode: it returns the session token in a
 * `set-auth-token` header, this file stores it in the Keychain, and every request carries
 * it in `Authorization`. The web build takes the same path, which keeps ONE auth story
 * instead of two — and localStorage is the honest equivalent there.
 */
const TOKEN_KEY = "auth.session-token";

let cachedToken: string | null = null;

/** Read once at start-up, before the first request needs it. */
export async function restoreAuthToken(): Promise<string | null> {
  cachedToken = await secureStore.get(TOKEN_KEY);
  return cachedToken;
}

export function getAuthToken(): string | null {
  return cachedToken;
}

/**
 * Adopts a token that arrived some way other than a fetch response — today that is the
 * deep link a social provider hands back (see src/app/lib/deep-links.ts).
 */
export async function adoptAuthToken(token: string): Promise<void> {
  await storeAuthToken(token);
}

async function storeAuthToken(token: string): Promise<void> {
  cachedToken = token;
  await secureStore.set(TOKEN_KEY, token);
}

export async function clearAuthToken(): Promise<void> {
  cachedToken = null;
  await secureStore.remove(TOKEN_KEY);
}

export const authClient = createAuthClient({
  baseURL: `${env.apiUrl}/v1/auth`,
  fetchOptions: {
    auth: { type: "Bearer", token: () => cachedToken ?? "" },
    onSuccess(ctx) {
      // Issued on sign-in AND rotated on refresh, so this is the only place that writes it.
      const token = ctx.response.headers.get("set-auth-token");
      if (token) void storeAuthToken(token);
    },
  },
});

export type AuthClient = typeof authClient;
